import { Alert, Button, Dialog } from "@/components/ui";
import { useKillSwitch, usePositions } from "@/hooks/api";
import { positionsApi } from "@/lib/api/endpoints";
import { toast } from "@/stores/toastStore";
import type { KillSwitchResult } from "@/types/api";
import { useState } from "react";

// 글자는 `inverse-label` 이에요(`static-white` 가 아니라). 다크의 `status-negative` 는 대비를
// 위해 밝은 빨강이라, 그 위에 흰 글자를 얹으면 2.7:1 로 이 앱에서 가장 중요한 버튼이 가장
// 안 읽혔어요. 반전 잉크는 라이트에서 흰색, 다크에서 거의 검정이라 두 테마 모두 4.5:1 을 넘어요.
const DANGER = { background: "var(--status-negative)", color: "var(--inverse-label)" };

/**
 * S4 — always on screen, one click plus a confirm.
 *
 * It pauses every rule and cancels every OPEN order/conditional order **that
 * Tranche registered**. It deliberately does NOT liquidate holdings: that is
 * a separate, second-confirmation action, because "stop trading" and "sell
 * everything" are different decisions and merging them would make the safety
 * control itself risky. Failures stay on screen — we never claim a clean sweep.
 *
 * It also does not touch orders/conditionals the operator placed directly in
 * the Toss app on a symbol Tranche never had one on — there's no "list every
 * conditional order" API to find those (Task 12 review I-6). `foreign_*_refs`
 * lists what it found and left alone; `notice` says the scan's boundary out
 * loud instead of letting the copy above imply a total sweep.
 */
/** `hasAccount` 는 버튼 노출 조건일 뿐 — 실행은 유저 스코프 전역이다. */
export function KillSwitch({ hasAccount }: { hasAccount: boolean }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [liquidateOpen, setLiquidateOpen] = useState(false);
  const [result, setResult] = useState<KillSwitchResult | null>(null);
  const killSwitch = useKillSwitch();
  const positions = usePositions();

  const run = () => {
    // 킬스위치는 유저 스코프 전역이다 — 계좌 선택과 무관하게 전부 멈춘다.
    killSwitch.mutate(undefined, {
      onSuccess: (data) => {
        setResult(data);
        setConfirmOpen(false);
        toast.warn("모든 규칙을 멈췄어요.");
      },
      onError: () => setConfirmOpen(false),
    });
  };

  const liquidate = async () => {
    const open = positions.data ?? [];
    setLiquidateOpen(false);
    const outcomes = await Promise.allSettled(open.map((p) => positionsApi.close(p.id)));
    const failed = outcomes.filter((o) => o.status === "rejected").length;
    if (failed === 0) toast.success("보유 종목 정리 주문을 모두 넣었어요.");
    else toast.error(`${failed}건은 정리 주문을 넣지 못했어요. 주문 내역에서 확인해 주세요.`);
    void positions.refetch();
  };

  return (
    <>
      <Button
        variant="solid"
        size="small"
        iconLeft="thunder"
        style={DANGER}
        disabled={!hasAccount || killSwitch.isPending}
        onClick={() => setConfirmOpen(true)}
      >
        전체 정지
      </Button>

      <Dialog
        open={confirmOpen}
        title="모든 규칙을 멈출까요?"
        width={400}
        onClose={() => setConfirmOpen(false)}
        secondaryAction={
          <Button variant="outlined" color="assistive" onClick={() => setConfirmOpen(false)}>
            닫기
          </Button>
        }
        primaryAction={
          <Button variant="solid" style={DANGER} disabled={killSwitch.isPending} onClick={run}>
            전체 정지하기
          </Button>
        }
      >
        <p className="text-body-2 text-label-normal">
          실행 중인 규칙을 모두 멈추고, 트랑슈가 낸 미체결 주문과 조건주문을 취소해요. 토스증권
          앱에서 직접 낸 주문·조건주문은 취소하지 않고 목록으로만 알려드려요.
        </p>
        <p className="mt-2 text-label-2 text-label-alternative">
          이미 보유한 종목은 팔지 않아요. 보유 종목까지 정리하려면 정지 후에 따로 확인해야 해요.
        </p>
      </Dialog>

      {result ? (
        <div className="fixed inset-x-0 bottom-24 z-40 mx-auto w-full max-w-xl px-4">
          <Alert
            tone={result.failures.length > 0 ? "cautionary" : "positive"}
            title="전체 정지를 실행했어요"
            onClose={() => setResult(null)}
            action={
              (positions.data?.length ?? 0) > 0 ? (
                <Button variant="outlined" size="small" onClick={() => setLiquidateOpen(true)}>
                  보유 종목도 정리하기
                </Button>
              ) : undefined
            }
          >
            <span className="tabular">
              규칙 {result.strategies_paused}개 정지 · 주문 {result.orders_canceled}건 취소 ·
              조건주문 {result.conditional_orders_canceled}건 취소
            </span>
            {/*
              A-1 — 토스증권 앱에서 직접 건 조건주문까지 지우면 다음 폭락 때 알게 돼요.
              그래서 우리가 만든 것만 취소하고, 남긴 건은 반드시 말해 줍니다.
            */}
            {result.foreign_open_order_refs.length > 0 ||
            result.foreign_conditional_order_refs.length > 0 ? (
              <p className="tabular mt-2 text-caption-1 text-label-alternative">
                토스증권 앱 주문 {result.foreign_open_order_refs.length}건 · 조건주문{" "}
                {result.foreign_conditional_order_refs.length}건은 그대로 뒀어요. {result.notice}
              </p>
            ) : null}
            {result.repaired_orders_skipped.length > 0 ? (
              <p className="tabular mt-2 text-caption-1 text-label-alternative">
                주문 {result.repaired_orders_skipped.length}건은 응답을 못 받아 상태를 추정으로
                복구한 건이라 취소하지 않았어요. 토스증권 앱에서 확인해 주세요.
              </p>
            ) : null}
            {result.failures.length > 0 ? (
              <ul className="mt-2 flex flex-col gap-1">
                {result.failures.map((failure) => (
                  <li key={failure} className="text-caption-1">
                    {failure}
                  </li>
                ))}
              </ul>
            ) : null}
          </Alert>
        </div>
      ) : null}

      <Dialog
        open={liquidateOpen}
        title="보유 종목을 전량 정리할까요?"
        width={400}
        onClose={() => setLiquidateOpen(false)}
        secondaryAction={
          <Button variant="outlined" color="assistive" onClick={() => setLiquidateOpen(false)}>
            닫기
          </Button>
        }
        primaryAction={
          <Button variant="solid" style={DANGER} onClick={() => void liquidate()}>
            전량 정리하기
          </Button>
        }
      >
        <p className="text-body-2 text-label-normal">
          지금 보유한 {positions.data?.length ?? 0}종목을 시장가로 정리하는 주문을 넣어요. 되돌릴 수
          없어요.
        </p>
      </Dialog>
    </>
  );
}
